/**
 * Ticket detail.
 *
 * Conversation pane (header + thread + composer) on the left; metadata
 * side panel on the right. Composer is the first optimistic mutation
 * surface — see hooks/use-messages.ts for the pattern.
 */

import { memo, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Mail } from 'lucide-react';

import type {
  Contract,
  Message,
  SlaContract,
  Ticket,
  TimeEntry,
  Uuid,
} from '@beacon/core';
import { humanizeMillis } from '@beacon/core';

import { AssigneeDropdown } from '@/components/beacon/assignee-dropdown.js';
import { Avatar } from '@/components/beacon/avatar.js';
import { ContractLogo } from '@/components/beacon/contract-logo.js';
import { Priority } from '@/components/beacon/priority.js';
import { PriorityDropdown } from '@/components/beacon/priority-dropdown.js';
import { SlaTicker } from '@/components/beacon/sla-ticker.js';
import { Status } from '@/components/beacon/status.js';
import { StatusDropdown } from '@/components/beacon/status-dropdown.js';
import { Progress } from '@/components/ui/progress.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useContractsById, useContractUsage } from '@/hooks/use-contracts.js';
import { useHotkeys } from '@/hooks/use-hotkeys.js';
import { useMessages, type CachedMessage } from '@/hooks/use-messages.js';
import { useNow } from '@/hooks/use-now.js';
import { recordRecentTicket } from '@/hooks/use-recent-tickets.js';
import { focusSidePanelSection } from '@/lib/focus-side-section.js';
import {
  useAssignTicket,
  useUpdateTicketPriority,
  useUpdateTicketStatus,
} from '@/hooks/use-ticket-mutations.js';
import { useTicket, useTicketTimeEntries } from '@/hooks/use-tickets.js';
import { useAssignableUsers } from '@/hooks/use-users.js';
import { useIsClient } from '@/hooks/use-viewer.js';
import { cn } from '@/lib/cn.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { slaSnapshotFor } from '@/lib/sla-display.js';

import { Composer, useRetrySend } from './Composer.js';
import { LogTimeDialog } from './LogTimeDialog.js';
import { ThreadView } from './ThreadView.js';

export function TicketDetail() {
  const isClient = useIsClient();
  if (isClient) return <PortalTicket />;
  return <StaffTicketDetail />;
}

function StaffTicketDetail() {
  const { number } = useParams();
  const ticketQuery = useTicket(number);
  const messagesQuery = useMessages(number);
  const timeEntriesQuery = useTicketTimeEntries(number);
  const contractsById = useContractsById();
  const usersQuery = useAssignableUsers();
  // No useNow at this level — the SLA pill in the header and the SLA
  // progress panel below each subscribe internally so typing in the
  // composer doesn't compete with second-by-second re-renders of the
  // whole page.
  const retry = useRetrySend(number ?? '');

  const updateStatus = useUpdateTicketStatus();
  const updatePriority = useUpdateTicketPriority();
  const assignTicket = useAssignTicket();
  const [logTimeOpen, setLogTimeOpen] = useState(false);

  // Hooks below MUST run on every render — never gate them behind
  // an early return for loading/error, otherwise React's rules-of-
  // hooks fires (different hook count between renders).
  const t: Ticket | undefined = ticketQuery.data;
  const contract = t ? contractsById.get(t.contractId) : undefined;

  // Push to the ⌘K palette's "Recent" stack on every successful load.
  // Re-runs only when the ticket number/subject/contract actually
  // changes; dedupes via recordRecentTicket so repeat visits stay
  // a single entry at the top of the stack.
  useEffect(() => {
    if (!t) return;
    recordRecentTicket({
      number: t.number,
      subject: t.subject,
      contractName: contract?.name ?? '',
    });
  }, [t?.number, t?.subject, contract?.name]);

  // Ticket-action shortcuts (Decisions §10):
  //   e — resolve   → optimistic status mutation, no dropdown click
  //   a — assign    → open assignee dropdown
  //   s — status    → open status dropdown
  //   t — log time  → focus hours section (Harvest deep-link lives there)
  // useHotkeys auto-skips when typing in input/textarea so these don't
  // fire while writing in the composer. Disabled while the ticket is
  // not yet loaded.
  useHotkeys(
    {
      e: () => {
        if (!t) return;
        if (t.status === 'resolved' || t.status === 'closed') return;
        updateStatus.mutate({ number: t.number, status: 'resolved' });
      },
      a: () => focusSidePanelSection('assignee'),
      s: () => focusSidePanelSection('status'),
      t: () => focusSidePanelSection('hours'),
    },
    { disabled: !t }
  );

  if (ticketQuery.isLoading) {
    return <StaffTicketSkeleton />;
  }
  if (ticketQuery.isError || !t) {
    return (
      <div className="p-8 text-ink-300">
        Ticket {number} not found.{' '}
        <Link to="/inbox" className="text-beacon-300 hover:underline">
          Back to inbox
        </Link>
      </div>
    );
  }

  const display = contract
    ? contractDisplay(contract)
    : { tag: '??', color: '#4A4F5A' };
  const messages = messagesQuery.data ?? [];

  return (
    <div className="flex h-full min-h-0">
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
          <div className="flex items-center gap-1.5 text-[12px] text-ink-300 mb-2">
            <Link to="/inbox" className="hover:text-ink-100">
              Inbox
            </Link>
            <ChevronRight size={12} className="text-ink-400" />
            <Link
              to={`/inbox?priority=${t.priority}`}
              className="hover:text-ink-100 capitalize"
            >
              {t.priority.toUpperCase()}
            </Link>
            <ChevronRight size={12} className="text-ink-400" />
            <span className="mono text-ink-200">{t.number}</span>
          </div>
          <div className="flex items-start gap-3">
            <ContractLogo contract={display} size={36} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[12.5px] text-ink-300">
                {contract && (
                  <>
                    <span className="font-medium text-ink-200">
                      {contract.name}
                    </span>
                    <span>·</span>
                    <span className="mono">{t.number}</span>
                    <span>·</span>
                    <span className="mono">{slaTierCaption(contract)}</span>
                  </>
                )}
              </div>
              <h1 className="text-[19px] font-semibold text-ink-100 leading-snug mt-0.5 pr-8">
                {t.subject}
              </h1>
              <div className="flex items-center flex-wrap gap-2 mt-2.5">
                <Status s={t.status} />
                <Priority p={t.priority} />
                <SlaTicker ticket={t} contract={contract} hidePlaceholder />
                <span className="text-[11.5px] text-ink-300 mono">
                  · Opened{' '}
                  {new Date(t.createdAt).toLocaleString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {messages.length}{' '}
                  {messages.length === 1 ? 'message' : 'messages'}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-6 py-6">
          {messagesQuery.isLoading ? (
            <ThreadSkeleton />
          ) : (
            <ThreadView messages={messages} onRetry={retry} />
          )}
        </div>

        <Composer ticketNumber={t.number} />
      </div>

      <aside className="w-[340px] shrink-0 border-l border-ink-600 bg-ink-900 overflow-auto p-5 space-y-5">
        <Section label="Assignee" sideSection="assignee">
          <AssigneeDropdown
            value={t.assigneeUserId as Uuid | null}
            users={usersQuery.data ?? []}
            loading={usersQuery.isLoading}
            onChange={(next) =>
              assignTicket.mutate({
                number: t.number,
                assigneeUserId: next,
              })
            }
          />
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section label="Status" sideSection="status">
            <StatusDropdown
              value={t.status}
              onChange={(next) =>
                updateStatus.mutate({
                  number: t.number,
                  status: next,
                })
              }
            />
          </Section>
          <Section label="Priority">
            <PriorityDropdown
              value={t.priority}
              onChange={(next) =>
                updatePriority.mutate({
                  number: t.number,
                  priority: next,
                })
              }
            />
          </Section>
        </div>

        {contract?.type === 'sla' && (
          <SlaProgressPanel ticket={t} contract={contract as SlaContract} />
        )}

        <HoursPanel
          entries={timeEntriesQuery.data ?? []}
          loading={timeEntriesQuery.isLoading}
          contract={contract}
          onLogTime={() => setLogTimeOpen(true)}
        />

        {contract && contract.type !== 'internal' && (
          <ContractUsagePanel contractId={contract.id} contractName={contract.name} />
        )}

        {/* Custom fields + Linked tickets — surfaces are visible so
            staff know they exist; data layer lands when the schema
            does (see SPEC §5 for the planned columns). */}
        <Section label="Custom fields">
          <div className="text-[12px] text-ink-400 italic">
            None set. Schema lands with the templates feature.
          </div>
        </Section>

        <Section label="Linked tickets">
          <div className="text-[12px] text-ink-400 italic">
            No links yet. Linking lands with the duplicate-resolution
            feature.
          </div>
        </Section>
      </aside>
      {t && (
        <LogTimeDialog
          open={logTimeOpen}
          onClose={() => setLogTimeOpen(false)}
          ticketNumber={t.number}
          ticketSubject={t.subject}
        />
      )}
    </div>
  );
}

/**
 * Self-ticking SLA progress panel — used in the ticket-detail side
 * panel. Subscribes to `useNow` internally so typing in the composer
 * doesn't compete with second-by-second re-renders of the whole page.
 * Memoised on (ticket, contract) — re-renders only when either changes
 * or the internal tick fires.
 */
const SlaProgressPanel = memo(function SlaProgressPanel({
  ticket,
  contract,
}: {
  ticket: Ticket;
  contract: SlaContract;
}) {
  const now = useNow(1000);
  const snap = slaSnapshotFor(ticket, contract, now);
  if (!snap) return null;
  const targetMs = contract.solutionTargetMinutes * 60_000;
  const value =
    snap.state === 'breached'
      ? 100
      : 100 -
        Math.min(
          100,
          Math.max(0, (snap.millisToDeadline / targetMs) * 100)
        );
  const tone =
    snap.state === 'breaching' || snap.state === 'breached'
      ? 'red'
      : snap.state === 'warn'
        ? 'amber'
        : 'green';
  return (
    <Section label="SLA — solution">
      <div className="bg-ink-800 border border-ink-600 rounded-md p-3">
        <div className="flex justify-between text-[12px] mb-1.5">
          <span className="text-ink-200">
            {contract.solutionTargetMinutes} min target
          </span>
          <span className="mono text-ink-100">
            {snap.state === 'breached'
              ? humanizeMillis(-snap.millisToDeadline) + ' overdue'
              : humanizeMillis(snap.millisToDeadline) + ' left'}
          </span>
        </div>
        <Progress label="SLA solution clock" value={value} tone={tone} />
      </div>
    </Section>
  );
});

function Section({
  label,
  children,
  action,
  sideSection,
}: {
  label: string;
  children: React.ReactNode;
  /** Optional right-aligned affordance (e.g., "Reassign", "+ Log"). */
  action?: React.ReactNode;
  /** Anchor key for Composer toolbar quick-actions to scroll/focus. */
  sideSection?: 'hours' | 'status' | 'assignee';
}) {
  return (
    <div data-side-section={sideSection}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-[10.5px] uppercase tracking-[0.08em] text-ink-300 font-semibold">
          {label}
        </div>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

/**
 * Hours on this ticket — total at the top, per-entry list below.
 * Time entries flow IN from Harvest; the +Log affordance deep-links
 * out to the contract's Harvest project so staff can log inline
 * without context-switching to a new tab manually. If the contract
 * has no harvestProjectId we hide the action.
 */
function HoursPanel({
  entries,
  loading,
  contract,
  onLogTime,
}: {
  entries: TimeEntry[];
  loading: boolean;
  contract: Contract | undefined;
  onLogTime: () => void;
}) {
  const total = entries.reduce(
    (sum, e) => sum + (e.hours as unknown as number),
    0
  );
  const harvestUrl =
    contract?.harvestProjectId
      ? `https://conducthq.harvestapp.com/time/?project=${encodeURIComponent(contract.harvestProjectId)}`
      : null;

  // Two affordances: in-Beacon manual entry (primary, always present)
  // and a deep-link out to Harvest when the contract has a project (so
  // staff who already work in Harvest can keep doing so).
  const action = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onLogTime}
        className="text-[11.5px] text-beacon-300 hover:underline inline-flex items-center gap-0.5 focus-ring rounded-sm"
        data-testid="ticket-log-time"
      >
        + Log time
      </button>
      {harvestUrl && (
        <a
          href={harvestUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] text-ink-400 hover:text-ink-200 hover:underline inline-flex items-center gap-0.5"
        >
          Harvest ↗
        </a>
      )}
    </div>
  );

  return (
    <Section label="Hours on this ticket" action={action} sideSection="hours">
      <div className="bg-ink-800 border border-ink-600 rounded-md p-3">
        <div className="flex items-baseline gap-2">
          <span className="mono text-[22px] font-semibold text-ink-100">
            {total.toFixed(2)}
          </span>
          <span className="text-[12px] text-ink-300">hours logged</span>
        </div>
        {loading ? (
          <div className="mt-2 text-[11.5px] text-ink-400 italic">
            Loading entries…
          </div>
        ) : entries.length === 0 ? (
          <div className="mt-2 text-[11.5px] text-ink-400 italic">
            No time entries linked yet — tag entries in Harvest with the
            ticket number to roll up here.
          </div>
        ) : (
          <ul className="mt-2 space-y-1.5 text-[11.5px]">
            {entries.map((e) => (
              <li key={e.id} className="flex justify-between gap-2">
                <span className="text-ink-200 truncate">
                  {e.userName} ·{' '}
                  <span className="text-ink-400 mono">
                    {new Date(e.spentDate).toLocaleString('en-AU', {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                </span>
                <span className="mono text-ink-300 shrink-0">
                  {(e.hours as unknown as number).toFixed(2)} h
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

/**
 * Contract usage roll-up — SLA shows current month vs allocation; BAU
 * shows lifetime vs budget. Renders nothing while loading; the
 * surface is informational so a brief skip beats a flash of placeholder.
 */
function ContractUsagePanel({
  contractId,
  contractName,
}: {
  contractId: string;
  contractName: string;
}) {
  const usageQuery = useContractUsage(contractId);
  const usage = usageQuery.data;
  if (!usage || usage.type === 'internal') return null;

  const used = usage.usedHours;
  const cap =
    usage.type === 'sla' ? usage.allocatedHours : usage.budgetHours;
  const percent = Math.min(100, Math.max(0, usage.percent));
  const tone: 'red' | 'amber' | 'green' =
    percent >= 100 ? 'red' : percent >= 80 ? 'amber' : 'green';

  return (
    <Section
      label={
        usage.type === 'sla' ? 'Contract — this month' : 'Contract — lifetime'
      }
    >
      <div className="bg-ink-800 border border-ink-600 rounded-md p-3">
        <div className="flex items-baseline justify-between text-[12px] mb-1.5">
          <span className="text-ink-200 truncate" title={contractName}>
            {contractName}
          </span>
          <span className="mono text-ink-100 shrink-0">
            {used.toFixed(2)} / {cap}h
          </span>
        </div>
        <Progress
          label={`${contractName} contract usage`}
          value={percent}
          tone={tone}
        />
        <div className="text-[11.5px] mono text-ink-400 mt-1.5">
          {percent}% used
          {usage.remainingHours < 0
            ? ` · ${Math.abs(usage.remainingHours).toFixed(1)}h overage`
            : ` · ${usage.remainingHours.toFixed(1)}h left`}
        </div>
      </div>
    </Section>
  );
}

/**
 * Mono caption for the header sub-meta. SLA contracts show their
 * solution-target tier so staff know which clock is ticking; BAU
 * and Internal say so plainly.
 */
function slaTierCaption(contract: Contract): string {
  if (contract.type === 'bau') return 'BAU';
  if (contract.type === 'internal') return 'INTERNAL';
  const mins = contract.solutionTargetMinutes;
  if (mins <= 60) return 'SLA — Priority Response';
  if (mins <= 4 * 60) return 'SLA — Standard Response';
  return 'SLA — Extended Response';
}

function ThreadSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <div className="w-[30px] h-[30px] rounded-full bg-ink-700 stripes opacity-30" />
          <div className="flex-1 h-20 rounded-lg bg-ink-700/40 stripes opacity-30" />
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------
 * Portal ticket — client-facing, read-only.
 *
 * Single column max-w-[720px] (Decision #14). Internal notes and
 * system events are stripped from the thread; remaining client +
 * staff messages render as flipped chat bubbles (client right with
 * beacon-amber tint, staff left with the Conduct lighthouse mark).
 * Footer hint instructs continuing the ticket by email — clients
 * never see a composer.
 * ---------------------------------------------------------------- */
function PortalTicket() {
  const { number } = useParams();
  const ticketQuery = useTicket(number);
  const messagesQuery = useMessages(number);

  if (ticketQuery.isLoading) {
    return <PortalTicketSkeleton />;
  }
  if (ticketQuery.isError || !ticketQuery.data) {
    return (
      <div className="p-8 text-ink-300">
        Ticket {number} not found.{' '}
        <Link to="/home" className="text-beacon-300 hover:underline">
          Back to home
        </Link>
      </div>
    );
  }
  const t: Ticket = ticketQuery.data;
  const messages = messagesQuery.data ?? [];
  const visible = messages.filter(
    (m) => m.type === 'client_email' || m.type === 'staff_email'
  );

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <div className="max-w-[720px] mx-auto px-8 pt-8 pb-20">
        <Link
          to="/home"
          className="inline-flex items-center gap-1 text-[12.5px] text-ink-300 hover:text-ink-100 mb-6 focus-ring rounded-md"
        >
          <ChevronLeft size={13} />
          Back to home
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-2 text-[11.5px] mono text-ink-300 mb-1">
            <span>{t.number}</span>
            <span>·</span>
            <span>
              Opened{' '}
              {new Date(t.createdAt).toLocaleString('en-AU', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink-100 leading-snug">
            {t.subject}
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <Status s={t.status} />
            <span className="text-[12.5px] text-ink-300">
              {t.assigneeUserId
                ? 'Conduct HQ team is on it.'
                : 'Awaiting triage.'}
            </span>
          </div>
        </div>

        {messagesQuery.isLoading ? (
          <ThreadSkeleton />
        ) : visible.length === 0 ? (
          <div className="text-center text-ink-300 text-[13px] py-8">
            No messages yet.
          </div>
        ) : (
          <div className="space-y-6">
            {visible.map((m) => (
              <PortalBubble key={m.id} m={m} />
            ))}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-ink-600 text-center">
          <div className="inline-flex items-center gap-2 text-[13px] text-ink-200">
            <Mail size={14} className="text-beacon-400" />
            <span>Reply by email to continue this ticket.</span>
          </div>
          <div className="text-[11.5px] text-ink-400 mono mt-1.5">
            support@conducthq.com · include{' '}
            <span className="text-ink-200">[{t.number}]</span> in the subject to
            keep the thread.
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalBubble({ m }: { m: CachedMessage | Message }) {
  const isInbound = m.type === 'client_email';
  const time = new Date(m.createdAt).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  // We don't expose staff names to clients in v1 — render the brand
  // mark instead and label the staff row "Conduct HQ".
  const author = isInbound ? friendlyAuthor(m) : 'Conduct HQ';
  return (
    <div className={cn('flex gap-3', isInbound && 'flex-row-reverse')}>
      <div className="shrink-0 pt-1">
        {isInbound ? (
          <Avatar name={author} size={32} />
        ) : (
          <span className="w-8 h-8 rounded-full bg-beacon-400 text-ink-900 inline-flex items-center justify-center font-bold text-[12px]">
            C
          </span>
        )}
      </div>
      <div className={cn('flex-1 min-w-0 max-w-[520px]')}>
        <div
          className={cn(
            'flex items-baseline gap-2 mb-1',
            isInbound && 'justify-end'
          )}
        >
          <span className="text-[13px] font-medium text-ink-100">
            {author}
          </span>
          <span className="text-[11.5px] text-ink-400 mono">{time}</span>
        </div>
        <div
          className={cn(
            'rounded-[12px] px-4 py-3 text-[13.5px] leading-relaxed whitespace-pre-wrap border',
            isInbound
              ? 'bg-beacon-400/10 border-beacon-400/25 text-ink-100'
              : 'bg-ink-800 border-ink-600 text-ink-100'
          )}
        >
          {m.bodyText}
        </div>
      </div>
    </div>
  );
}

function friendlyAuthor(m: CachedMessage | Message): string {
  // We don't store the inbound sender's display name on the Message
  // DTO yet — fall back to a generic "You" for the client side. The
  // brand mark on the staff side carries the identity that matters.
  if (m.authorUserId) return m.authorUserId.replace(/^user-/, '');
  return 'You';
}

/**
 * Staff ticket-detail skeleton. Mirrors the real layout shell — header,
 * thread, side panel — so the page doesn't reflow when data lands.
 * INTERACTION_SPEC §2: ticket thread = 3 message blocks; side panel
 * blocks resolve independently.
 */
function StaffTicketSkeleton() {
  return (
    <div className="flex h-full min-h-0" aria-busy="true">
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
          <Skeleton className="h-3 w-44 mb-2" />
          <Skeleton className="h-5 w-2/3 max-w-[480px] mb-2" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16" inline />
            <Skeleton className="h-5 w-12" inline />
            <Skeleton className="h-5 w-20" inline />
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <ThreadSkeleton />
        </div>
      </div>
      <aside className="w-80 shrink-0 border-l border-ink-600 bg-ink-850 flex flex-col divide-y divide-ink-600">
        {(['Assignee', 'Status', 'SLA', 'Hours'] as const).map((label) => (
          <section key={label} className="px-4 py-3 space-y-2">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-4 w-3/4" />
          </section>
        ))}
      </aside>
    </div>
  );
}

/**
 * Portal ticket skeleton — airy single-column shape matching the
 * client-portal aesthetic (Decision #14).
 */
function PortalTicketSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <div className="max-w-[720px] mx-auto px-8 pt-8 pb-20">
        <Skeleton className="h-3 w-28 mb-6" />
        <div className="mb-8 space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-3 w-40" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'flex',
                i % 2 === 0 ? 'justify-start' : 'justify-end'
              )}
            >
              <div className="w-[80%] space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

